import {
  Component,
  h,
  Host,
  Element,
  State,
  Watch,
  Event,
  EventEmitter,
  Prop,
  Listen,
  Build,
  getAssetPath,
  readTask,
  writeTask,
  Method,
  VNode,
  ComponentInterface,
} from '@stencil/core';
import algoliasearch, { SearchClient as AlgoliaClient } from 'algoliasearch';
import { clientFetch } from '../../utils/fetch';
import { ComponentStore } from '../../utils/store/component-store';
import { debounce } from '../../utils/throttle';
import { displayTransition } from '../../utils/transitions';
import type {
  InputChangeEventDetail,
  SelectChangeEventDetail,
  AlgoliaResults,
  SearchIndex,
  MyAccountLink,
  AloliaSearchResultDetail,
  AlgoliaMultiResult,
  MyAccountUser,
  MyAccountData,
  NavItemEventDetail,
  AlgoliaResultHit,
  AlgoliaNetworkError,
  AlgoliaMultiResults,
} from '../../interface';

// the 'breakpoints' where different segments of content go into or out-of the burger / overflow menu
const THRESHOLDBREAKS = {
  about: 0,
  main: 1,
  login: 2,
  search: 3,
  icon: 4,
};

/**
 * Nanopore digital global navigation and search bar.
 * Incorporates MyAccount / SSO states (messages / cart / user name etc).
 * Incorporates Algolia search.
 *
 * @slot main - main nav items. Should be used with nano-nav-item's
 * @slot icon - nano-nav-item's displayed alongside icon links that come from MyAccount
 * @slot loggedin - nano-nav-item's to display when users are signed in, alongside links from from MyAccount (within the overflow menu panel).
 * @slot about - nano-nav-item's to display with about links that come from MyAccount
 * @slot site - nano-nav-item's to display with site links that come from MyAccount
 * @slot overflow - items to always display in the overflow menu
 * @slot search - when not using myaccount search OR show-search="false" you can utilise the search slot
 * @slot promotion - a free area for any promotional content, displayed within the overflow menu panel
 * @slot logo - when show-logo="false" you can utilise the logo slot
 * @slot - if using search functionality, place a `<nano-search-results />` element in the default slot
 *
 * @part burger - the overflow / burger ui element
 * @part burger-menu - the overflow / burger menu
 * @part burger-actions-bar - the top bar of the overflow / burger menu
 * @part burger-content - the main content area of the overflow / burger menu
 * @part sticker - the `nano-sticker` wrapping the main nav-bar
 * @part bars - the main wrapper around the `main-bar` and `search-bar` nav bars
 * @part main-bar - the main nav menu bar
 * @part logo-link - the link wrapping the logo
 * @part search-bar - the search bar that appears under the main nav menu bar
 */
@Component({
  tag: 'nano-global-nav',
  styleUrl: 'style/global-nav.scss',
  shadow: true,
  assetsDirs: ['assets'],
})
export class GlobalNav implements ComponentInterface {
  // position and dimension state
  private io: IntersectionObserver;
  private debounceResize: () => {};
  private debounceSetResizingState: () => {};
  private currSize: number = 0;
  private thresholdsGoingUp: boolean = false;
  private THRESHOLDLIMIT = Object.keys(THRESHOLDBREAKS).length;

  // menu
  private menuDiv: HTMLElement;
  private menuBtn: HTMLButtonElement;
  private menuWrapDiv: HTMLDivElement;
  private menuContentDiv: HTMLDivElement;
  private menuSelectedItems: NodeListOf<HTMLNanoNavItemElement>;

  // search
  private searchBarEl: HTMLDivElement;
  private searchInput: HTMLNanoInputElement;
  private algoliaClient: AlgoliaClient;
  private currentIndex: SearchIndex;
  private autocompleteResults: AlgoliaResults | null;
  private autocompleteEles: Array<HTMLAnchorElement | HTMLButtonElement> = [];
  private currAIndex: number = -1;
  private algoliaSearchResults: AlgoliaMultiResults | null;
  private scopeSearch: AlgoliaMultiResults | null;

  // general
  private mainBarDiv: HTMLElement;
  // private barsDiv: HTMLDivElement;
  // private siteContent: HTMLDivElement;
  private userLinkPanel: HTMLDivElement;
  private isLoggedIn: boolean = false;

  private myAccData: MyAccountData = null;

  // myaccount links
  private aboutNavItms: MyAccountLink[] = [];
  private siteNavItms: MyAccountLink[] = [];
  private loggedInNavItms: MyAccountLink[] = [];

  constructor() {
    this.assessSlottedContent = debounce(
      this.assessSlottedContent.bind(this),
      10
    );
  }

  // user profile link
  @State() userProfileUrl: string = '';

  // has slot tests
  private mo?: MutationObserver;
  @State() hasLoggedinSlot: boolean = false;
  @State() hasSiteSlot: boolean = false;
  @State() hasPromotionSlot: boolean = false;

  // slot item number tests
  @State() aboutSlotLen: number = 0;
  @State() iconSlotLen: number = 0;
  @State() mainSlotLen: number = 0;
  @State() overflowSlotLen: number = 0;
  @State() searchSlotLen: number = 0;

  // threshold / sizing
  @State() thresholdReady: boolean = false;
  @State() remoteDataReady: boolean = false;
  @State() ready: boolean = false;
  @State() isResizing: boolean = false;
  @State() intersectRatio: number;
  @State() threshold: number = this.THRESHOLDLIMIT;

  // modals state
  @State() modalOpen: boolean = false;
  @State() modalIsOpen: boolean = false;
  @State() searchBarShown: boolean = false;
  @State() scrollingUp: boolean = true;
  @State() showAutocomplete: boolean = false;
  @State() secondaryMenuOpen: HTMLNanoNavItemElement = null;
  @State() userMenuOpen: boolean = false;
  @State() menuFullScreen: boolean = false;

  // search
  @State() searchLoading: boolean = false;
  @State() searchValInternal: string = '';
  @State() internalSearchIndeces: Array<SearchIndex> = [];

  @Element() private el: HTMLNanoGlobalNavElement;

  /**
   * Connect to local, dev, test or prod MyAccount
   * environments to retrieve global nav / sso data.
   * Alternatively, you can use the `ssoDataUrl` prop.
   */
  @Prop() env?: 'local' | 'dev' | 'test' | 'prod' = 'prod';

  /**
   * A base url to retrieve global nav / sso data.
   * `nav_bar_data.json` will be appended to this url.
   * An alternative to the pre-defined `env` urls
   */
  @Prop() ssoDataUrl?: string;

  /**
   * An alternative to both 'env' and 'ssoDataUrl'.
   * You can directly provide a JSON object of myaccount data.
   * (e.g. https://myaccount.nanoporetech.com/nav_bar_data.json)
   * This is useful for SSR
   */
  @Prop() myAccountData: MyAccountData | null;
  @Watch('myAccountData')
  handleMyAccountData() {
    if (!this.myAccountData) return;

    if (!this.myAccountData.user || !this.myAccountData.links) return;

    this.myAccData = this.myAccountData;
    if (this.myAccData.user.id) {
      this.isLoggedIn = true;
      this.myAccountUser = this.myAccData.user;
    }

    this.processMyAccLinks();
    this.processMyAccData();
    this.remoteDataReady = true;
  }

  /**
   * Where to take user after SSO login
   */
  @Prop() ssoRedirect: string = encodeURIComponent(window.location.href);

  /**
   * Whether to attempt to get global nav / sso data from MyAccount
   */
  @Prop() getMyAccountData: boolean = true;

  /**
   * Active MyAccount link sections. By default all will show.
   */
  @Prop() activeMyAccountSections: (
    | 'about'
    | 'loggedin'
    | 'sites'
    | 'icons'
  )[] = null;

  /**
   * Whether to show search functionality
   */
  @Prop() showSearch: boolean = true;

  /**
   * Whether to show Nanopore logo
   */
  @Prop() showLogo: boolean = true;

  /**
   * The link to put on the logo
   */
  @Prop() logoUrl: string = location.protocol + '//' + location.host;

  /**
   * An array of Algolia search indexes (and optional display names). Will be automatically populated from MyAccount
   */
  @Prop() searchIndeces: Array<SearchIndex> = [];

  /**
   * Logged in user details automatically populated from MyAccount
   */
  @Prop({ mutable: true }) myAccountUser: MyAccountUser = null;

  /**
   * An Algolia App ID key. Will be automatically populated from MyAccount
   */
  @Prop({ mutable: true }) searchAppId: string;

  /**
   * An Algolia API Key. Will be automatically populated from MyAccount
   */
  @Prop({ mutable: true }) searchApiKey: string;

  /**
   * A search value to passed to Algolia to programmatically get or to preload search results
   */
  @Prop({ mutable: true }) searchValue: string = '';

  /**
   * Algolia search index to programmatically set or to set an initial index
   */
  @Prop({ mutable: true }) searchIndex: string = 'all_prod_en';

  /**
   * MyAccount store cart count.
   */
  @Prop({ mutable: true }) cartCount: number = 0;

  /**
   * MyAccount un-read message count.
   */
  @Prop({ mutable: true }) msgCount: number = 0;

  /**
   * Url to the Nanopore store cart. Will default to the url supplied by MyAccount
   */
  @Prop({ mutable: true }) cartUrl: string = null;

  /**
   * Url to the Nanopore message center. Will default to the url supplied by MyAccount
   */
  @Prop({ mutable: true }) msgUrl: string = null;

  /**
   * Emitted when the global nav is ready.
   */
  @Event() nanoIsReady!: EventEmitter<void>;

  /**
   * Emitted when search results are successfully returned from Algolia.
   */
  @Event() nanoSearchResult!: EventEmitter<AloliaSearchResultDetail>;

  /**
   * Emitted when algolia returns an error.
   */
  @Event() nanoSearchError!: EventEmitter<AlgoliaNetworkError>;

  /**
   * Emitted when search field is cleared.
   */
  @Event() nanoSearchReset!: EventEmitter<AloliaSearchResultDetail>;

  /**
   * Submit the search form (a search value must be present)
   */
  @Method()
  async submitSearch() {
    this.onSearchSubmit();
    this.showAutocomplete = false;
    return;
  }

  /**
   * Manually open or close the overflow (burger) menu
   * @param openClose - optionally force open (true) or close (false)
   */
  @Method()
  async toggleOverflowMenu(openClose?: boolean) {
    if (openClose === true || (openClose === undefined && !this.modalIsOpen)) {
      this.menuOpen();
    } else {
      this.menuClose(null, true);
    }
  }

  @Watch('env')
  @Watch('ssoDataUrl')
  async getMyAccData() {
    if (!!this.myAccData) return;

    switch (this.env) {
      case 'prod':
        this.userProfileUrl = 'https://myaccount.nanoporetech.com/';
      case 'local':
      case 'dev':
      case 'test':
      default:
        this.userProfileUrl = 'https://myaccount.nanoporetech.com/';
        break;
    }

    if (!this.getMyAccountData || this.myAccData) {
      this.remoteDataReady = true;
      return;
    }

    let url = this.ssoDataUrl;

    if (!url) {
      switch (this.env) {
        case 'dev':
          url = 'https://myaccount-dev.nanoporetech.com/';
          break;
        case 'test':
          url = 'https://myaccount-test.nanoporetech.com/';
          break;
        default:
          url = 'https://myaccount.nanoporetech.com/';
          break;
      }
    }

    const config = { timeout: 5000 };

    try {
      if (this.env === 'local') {
        this.myAccData = (await import(
          `./assets/local-my-account.json`
        )) as unknown as MyAccountData;
      } else {
        this.myAccData = await clientFetch(url + 'nav_bar_data.json', config);
      }
      if (this.myAccData.user.id) {
        this.isLoggedIn = true;
        this.myAccountUser = this.myAccData.user;
      }
      this.processMyAccLinks();
      this.processMyAccData();
      setTimeout(() => (this.remoteDataReady = true), 300);
    } catch (e) {
      console.error(e);
      this.remoteDataReady = true;
    }
  }

  @Watch('searchIndex')
  currentSelectedIndex() {
    if (!this.internalSearchIndeces.length) {
      return;
    }
    if (!this.searchIndex) return this.internalSearchIndeces[0];

    const foundIndex = this.internalSearchIndeces.find(
      (index) => index.index === this.searchIndex
    );

    if (!foundIndex) {
      this.currentIndex = this.internalSearchIndeces[0];
      console.error('index not found');
      return;
    }

    this.currentIndex = foundIndex;
    return foundIndex;
  }

  @Watch('searchValInternal')
  changeInternalSearchVal() {
    if (this.searchValInternal !== this.searchValue)
      this.searchValue = this.searchValInternal;
  }

  @Watch('searchApiKey')
  @Watch('searchAppId')
  initAlgoliaClient() {
    if (!this.searchAppId || !this.searchApiKey) return;

    this.algoliaClient = algoliasearch(this.searchAppId, this.searchApiKey);
    this.addAlgoliaIndeces();
  }

  @Watch('searchIndeces')
  addIndeces() {
    this.internalSearchIndeces = [
      ...this.internalSearchIndeces,
      ...this.searchIndeces,
    ];
  }

  @Watch('internalSearchIndeces')
  addAlgoliaIndeces() {
    if (!this.algoliaClient || !this.internalSearchIndeces.length) return;

    this.internalSearchIndeces.forEach((searchIndex) => {
      searchIndex.alogliaIndex = this.algoliaClient.initIndex(
        searchIndex.index
      );
    });
    if (!this.searchIndex)
      this.searchIndex = this.internalSearchIndeces[0].index;
  }

  @Watch('threshold')
  thresholdChange(newThreshold, oldThreshold) {
    if (newThreshold > oldThreshold) {
      // we've resized to larger screen.
      this.thresholdsGoingUp = true;
      this.setResizingState(true);
    }
  }

  /**
   * Primary logic for resizing changes.
   * When content doesn't fit - we loop down through break points until it does
   */
  @Watch('intersectRatio')
  ratioChange() {
    if (this.intersectRatio < 1 && this.threshold > 0) {
      // content doesn't fit
      if (this.thresholdsGoingUp) this.setResizingState(true);
      this.threshold--;
      setTimeout(() => this.ratioChange(), 500);
    } else {
      this.thresholdReady = true;

      // content now fits so add / remove context classes
      // on nav-items depending on whether they're added to bar or burger.

      let menuItems: Array<Element>, barItems: Array<Element>;
      readTask(() => {
        menuItems = [
          ...Array.from(this.el.querySelectorAll('nano-nav-item[slot="site"]')),
          ...Array.from(
            this.el.querySelectorAll('nano-nav-item[slot="overflow"]')
          ),
        ];
        barItems = [];

        if (this.threshold < this.THRESHOLDLIMIT - THRESHOLDBREAKS.icon)
          menuItems.push(
            ...Array.from(
              this.el.querySelectorAll('nano-nav-item[slot="icon"]')
            )
          );
        else
          barItems.push(
            ...Array.from(
              this.el.querySelectorAll('nano-nav-item[slot="icon"]')
            )
          );

        if (this.threshold < this.THRESHOLDLIMIT - THRESHOLDBREAKS.login)
          menuItems.push(
            ...Array.from(
              this.el.querySelectorAll('nano-nav-item[slot="loggedin"]')
            )
          );
        else
          barItems.push(
            ...Array.from(
              this.el.querySelectorAll('nano-nav-item[slot="loggedin"]')
            )
          );

        if (this.threshold < this.THRESHOLDLIMIT - THRESHOLDBREAKS.main)
          menuItems.push(
            ...Array.from(
              this.el.querySelectorAll('nano-nav-item[slot="main"]')
            )
          );
        else
          barItems.push(
            ...Array.from(
              this.el.querySelectorAll('nano-nav-item[slot="main"]')
            )
          );

        if (this.threshold < this.THRESHOLDLIMIT - THRESHOLDBREAKS.about)
          menuItems.push(
            ...Array.from(
              this.el.querySelectorAll('nano-nav-item[slot="about"]')
            )
          );
        else
          barItems.push(
            ...Array.from(
              this.el.querySelectorAll('nano-nav-item[slot="about"]')
            )
          );
      });

      writeTask(() => {
        menuItems.forEach((item) => {
          item.classList.add('nano-global-nav-menu');
          item.classList.remove('nano-global-nav-bar');
        });
        barItems.forEach((item) => {
          item.classList.remove('nano-global-nav-menu');
          item.classList.add('nano-global-nav-bar');
        });
      });
    }
  }

  @Watch('thresholdReady')
  @Watch('remoteDataReady')
  assessReady() {
    if (this.thresholdReady && this.remoteDataReady) {
      this.ready = true;
    }
  }

  @Watch('remoteDataReady')
  remoteReady() {
    this.attachIO();
  }

  @Watch('ready')
  isReady() {
    this.nanoIsReady.emit();
  }

  /**
   * Add / remove events when autocomplete results show / hide.
   */
  @Watch('showAutocomplete')
  setupAutocompleteBlur() {
    this.searchInput.clientWidth; // force reflow
    if (this.showAutocomplete) {
      this.el.shadowRoot.addEventListener('click', this.onAutocompleteBlur);
    } else {
      this.el.shadowRoot.removeEventListener('click', this.onAutocompleteBlur);
    }
  }

  @Watch('secondaryMenuOpen')
  closeSearchBar() {
    if (!!this.secondaryMenuOpen && this.searchBarShown) this.searchbarHide();
  }

  /**
   * As relevant slotted items change, reset resizing breakpoints
   */
  @Watch('aboutSlotLen')
  @Watch('iconSlotLen')
  @Watch('mainSlotLen')
  @Watch('overflowSlotLen')
  @Watch('searchSlotLen')
  slotChange() {
    this.threshold = this.THRESHOLDLIMIT;
    if (this.ready) this.ratioChange();
  }

  // General classes for visuals

  private thresholdClasses() {
    const classes = [];
    let i = 0;
    for (i; i < this.threshold; i++) classes.push('threshold-' + (i + 1));
    return classes;
  }

  /**
   * provides 'isResizing' - we use this to fade content out on 'breakpoint' increase
   * (when the screen size increases). Otherwise the stepping through of
   * breakpoints to calculate if content currently fits shows on screen (looks ugly)
   */
  private setResizingState = (state: boolean = false) => {
    if (typeof this.debounceSetResizingState === 'undefined') {
      this.debounceSetResizingState = debounce(this.setResizingState, 600);
    }
    if (state) {
      this.isResizing = true;
      this.debounceSetResizingState();
    } else {
      this.isResizing = false;
      this.thresholdsGoingUp = false;
    }
  };

  // Global DOM Events

  private onWindowResize() {
    if (
      this.threshold < this.THRESHOLDLIMIT &&
      window.innerWidth > this.currSize
    ) {
      this.threshold = this.THRESHOLDLIMIT;
      this.ratioChange();
    }
    this.currSize = window.innerWidth;
    this.searchBarShown = false;
  }

  // Burger / Overflow Menu

  // listen to nav-item secondary open / close events primarily for resizing the menu
  @Listen('nanoOpen')
  secondaryOpen(ev: CustomEvent & { target: HTMLNanoNavItemElement }) {
    if (!(ev.detail as NavItemEventDetail).secondaryMenu) return;
    ev.stopPropagation();
    this.secondaryMenuOpen = ev.target;

    if (!this.menuSelectedItems) {
      this.menuSelectedItems = this.el.querySelectorAll(
        'nano-nav-item.selected'
      );
      this.menuSelectedItems.forEach((i) => i.classList.remove('selected'));
      this.menuSelectedItems.forEach((i) => (i.selected = false));
    }

    this.menuContentDiv.style.minHeight =
      (ev.detail as NavItemEventDetail).secondaryMenu.scrollHeight + 'px';
  }

  @Listen('nanoClose')
  secondaryClose(ev: CustomEvent & { target: HTMLNanoNavItemElement }) {
    if (!(ev.detail as NavItemEventDetail).secondaryMenu) return;
    ev.stopPropagation();

    setTimeout(() => {
      if (this.secondaryMenuOpen === ev.target && this.menuSelectedItems) {
        this.secondaryMenuOpen = null;
        this.menuContentDiv.style.minHeight = '';
        this.menuSelectedItems.forEach((i) => i.classList.add('selected'));
        this.menuSelectedItems.forEach((i) => (i.selected = true));
        this.menuSelectedItems = null;
      }
    }, 200);
  }

  private onMenuBtnKeyDown = (ev: KeyboardEvent) => {
    switch (ev.key) {
      case 'Enter':
      case ' ':
        this.modalOpen ? this.menuClose(ev, true) : this.menuOpen();
        break;
    }
  };

  private onMenuBtnClick = (ev: MouseEvent) => {
    this.menuClose(ev, true);
  };

  private menuOpen = (e?: MouseEvent) => {
    if (e) e.preventDefault();

    this.menuDiv.style.display = 'block';
    // need to open menu in stages
    // wait for re-flow after changing display
    // wait for repaint after animate, then focus

    this.modalOpen = true;
    this.menuDiv.clientWidth; // force reflow

    this.menuWrapDiv.focus({ preventScroll: true });
    if (
      this.threshold < 2 ||
      this.menuWrapDiv.scrollHeight > window.innerHeight
    ) {
      document.body.style.overflow = 'hidden';
      this.menuFullScreen = true;
    } else document.body.style.overflowX = 'hidden';

    setTimeout(() => (this.modalIsOpen = true), 300);
  };

  private onMenuHidden = () => {
    this.menuDiv.removeEventListener('transitionend', this.onMenuHidden);

    document.body.style.overflow = '';
    this.menuDiv.style.display = 'none';
    this.modalIsOpen = false;
    this.menuFullScreen = false;

    setTimeout(() => {
      if (document.activeElement === document.body && this.menuBtn)
        this.mainBarDiv.focus({ preventScroll: true });
    }, 50);
  };

  @Listen('click', { target: 'body' })
  menuClose(
    ev?: FocusEvent | MouseEvent | KeyboardEvent | TouchEvent,
    force: boolean = false
  ) {
    if (!this.modalIsOpen) return;

    if (force) {
      this.modalOpen = false;
      this.menuDiv.addEventListener('transitionend', this.onMenuHidden);
      return;
    }

    if (
      !ev
        .composedPath()
        .some(
          (el) =>
            el === this.menuContentDiv ||
            (el as Element).classList?.contains('gn-menu_actions')
        )
    ) {
      requestAnimationFrame(() => {
        if (!!this.secondaryMenuOpen) return;

        this.modalOpen = false;
        this.menuDiv.addEventListener('transitionend', this.onMenuHidden);
      });
    }
  }

  private subMenuClose = (ev?: MouseEvent) => {
    ev.preventDefault();
    (ev.target as HTMLNanoNavItemElement).closest('nano-nav-item').open = false;
  };

  // User profile menu

  private onUserBtnClick = (ev: MouseEvent) => {
    ev.preventDefault();
    this.userMenuOpen = !this.userMenuOpen;
  };

  private onUserBtnKeyDown = (ev: KeyboardEvent) => {
    switch (ev.key) {
      case 'Enter':
      case ' ':
        this.userMenuOpen = !this.userMenuOpen;
        break;
    }
  };

  private onUserMenuBlur = (ev: FocusEvent) => {
    if (!ev.relatedTarget) {
      this.userMenuOpen = false;
      return;
    }
    if (!!(ev.relatedTarget as HTMLElement).closest('.user-links-panel'))
      return false;
    this.userMenuOpen = false;
  };

  @Watch('userMenuOpen')
  async handleUserMenu() {
    await displayTransition(this.userLinkPanel, 'show', this.userMenuOpen);
    if (this.userMenuOpen) {
      this.userLinkPanel.focus();
      this.userLinkPanel.addEventListener('focusout', this.onUserMenuBlur);
    } else {
      this.userLinkPanel.removeEventListener('focusout', this.onUserMenuBlur);
      this.el.focus();
    }
  }

  // Search bar (smaller screens / mobile)

  private onSearchBtnClick = (ev: MouseEvent) => {
    ev.preventDefault();
    if (!this.searchBarShown) this.searchbarShow();
    else this.searchbarHide();
  };

  private onSearchBtnKeyDown = (ev: KeyboardEvent) => {
    switch (ev.key) {
      case 'Enter':
      case ' ':
        this.searchBarShown ? this.searchbarHide() : this.searchbarShow();
        break;
    }
  };

  private searchbarShow() {
    this.searchBarShown = true;
    this.searchBarEl.addEventListener('transitionend', this.onSearchBarShown);
    this.searchBarEl.style.display = 'block';

    setTimeout(() => {
      const widgetHeight =
        Math.floor(
          this.searchBarEl.querySelector('.search-widget').scrollHeight
        ) - 1;
      this.searchBarEl.style.height = widgetHeight.toString() + 'px';
    }, 50);
  }

  private onSearchBarShown = () => {
    this.searchBarEl.removeEventListener(
      'transitionend',
      this.onSearchBarShown
    );
    if (this.searchInput) this.searchInput.setFocus();
    this.searchBarEl.style.overflow = 'visible';
  };

  private searchbarHide = (ev?: FocusEvent) => {
    if (
      ev &&
      ev.relatedTarget &&
      (ev.relatedTarget as HTMLElement).closest('#global-nav-search-bar')
    )
      return;

    this.showAutocomplete = false;
    if (this.searchValInternal.length && ev) return;

    this.searchBarEl.addEventListener('transitionend', this.onSearchBarHidden);
    this.searchBarEl.style.overflow = 'hidden';
    this.searchBarEl.style.height = '0px';
  };

  private onSearchBarHidden = () => {
    this.searchBarEl.removeEventListener(
      'transitionend',
      this.onSearchBarHidden
    );
    this.searchBarEl.style.display = 'none';
    this.searchBarShown = false;
  };

  // Algolia search

  private autocompleteSnippet(hit: AlgoliaResultHit) {
    const lookFor = ['title']; // , 'body'
    if (hit._snippetResult) {
      const found = lookFor.find(
        (prop) =>
          hit._snippetResult[prop] &&
          hit._snippetResult[prop].matchLevel !== 'none'
      );

      if (found) {
        if (found !== 'title')
          return (
            (hit.title.length > 15
              ? hit.title.substring(0, 15) + ' ...'
              : hit.title) +
            ' ' +
            hit._snippetResult[found].value
          );
        else return hit._snippetResult['title'].value;
      }
    }
    return hit.title;
  }

  private onSearchInput = (ev: CustomEvent<InputEvent>) => {
    this.searchValInternal = this.searchInput.value;

    if (!this.isSearchValSet()) {
      this.searchLoading = false;
      return;
    }

    if (ev.detail.type === 'input') {
      this.showAutocomplete = false;
      this.searchLoading = true;
    }
  };

  private onSearchChange = async (ev?: CustomEvent<InputChangeEventDetail>) => {
    this.searchValInternal = this.searchValue = ev
      ? ev.detail.value
      : this.searchValInternal;
    this.showAutocomplete = false;
    this.autocompleteResults = null;

    if (!this.isSearchValSet()) {
      if (!this.searchValInternal.length) {
        this.nanoSearchReset.emit();
      }
      this.searchLoading = false;
      return;
    }
    if (!this.currentIndex) this.currentSelectedIndex();

    try {
      this.autocompleteResults = this.processSearchResults(
        (await this.currentIndex.alogliaIndex.search(this.searchValInternal, {
          attributesToSnippet: ['body:5', 'title:8'],
          hitsPerPage: 5,
          filters: 'created > ' + Math.floor((Date.now() - 63115200000) / 1000), // only get last 2 years
        })) as any
      ) as AlgoliaResults;
    } catch (e) {
      console.error(e);
    }

    this.scopeSearch = null;
    this.scopeSearch = await this.onSearchSubmit(null, false);

    this.searchLoading = false;
    this.showAutocompleteResults();
  };

  private onSearchSubmit = async (ev?, emit: boolean = true) => {
    if (ev) ev.preventDefault();
    if (!this.isSearchValSet()) return;
    this.searchLoading = true;
    this.algoliaSearchResults = null;

    if (!this.currentIndex) this.currentSelectedIndex();

    const queries = this.internalSearchIndeces.map((index) => {
      return {
        indexName: index.index,
        query: this.searchValInternal,
        facets: index.filters,
        hitsPerPage: 10,
        filters: 'created > ' + Math.floor((Date.now() - 63115200000) / 1000), // only get last 2 years
      };
    });

    try {
      this.algoliaSearchResults = this.processSearchResults(
        (await this.algoliaClient.multipleQueries(queries)) as any
      ) as AlgoliaMultiResults;
    } catch (e) {
      this.nanoSearchError.emit(e as AlgoliaNetworkError);
    }
    this.searchLoading = false;
    if (!this.algoliaSearchResults) return;

    this.algoliaSearchResults.results.map((result, i) => {
      result.indexName = this.internalSearchIndeces[i].name;
      result.selected =
        this.currentIndex.name === this.internalSearchIndeces[i].name;
      result.domain = this.currentIndex.domain || null;
      result.domains = this.myAccData.domains || null;
      result.allGroup = !!this.internalSearchIndeces[i].allGroup;
      result.filters = this.internalSearchIndeces[i].filters;
    });

    if (emit) {
      this.nanoSearchResult.emit({
        results: this.algoliaSearchResults.results,
        client: { apiKey: this.searchApiKey, appId: this.searchAppId },
      });
      this.showAutocomplete = false;
    }
    return this.algoliaSearchResults;
  };

  private onSearchIndexChange = async (
    index: CustomEvent<SelectChangeEventDetail> | string
  ) => {
    const ev = index as CustomEvent<SelectChangeEventDetail>;
    let indexStr = index as string;

    if (!this.currentIndex) this.currentSelectedIndex();

    if (ev.detail && ev.detail.value.length)
      indexStr = ev.detail.value as string;
    if (!indexStr || !indexStr.length) return;
    if (this.currentIndex.index === indexStr) return;

    const selectedIndex = this.internalSearchIndeces.find(
      (index) => index.index === indexStr
    );
    if (!selectedIndex || !selectedIndex.alogliaIndex) return;

    this.searchIndex = selectedIndex.index;
    if (this.isSearchValSet()) await this.onSearchChange();
  };

  private isSearchValSet() {
    if (this.searchValInternal.length < 3) return false;
    return true;
  }

  private switchIndexSubmit = async (index: string) => {
    if (this.isSearchValSet()) {
      await this.onSearchIndexChange(index);
      this.onSearchSubmit();
    } else this.onSearchIndexChange(index);
  };

  // Autocomplete

  @Watch('showAutocomplete')
  resetHitFocus() {
    this.currAIndex = -1;
  }

  private onAutocompleteBlur = (ev: MouseEvent) => {
    if (!this.showAutocomplete || !this.autocompleteResults) return;
    if (ev && ev.target && (ev.target as HTMLElement).closest('.search-widget'))
      return;
    this.showAutocomplete = false;
  };

  private showAutocompleteResults = () => {
    if (!this.autocompleteResults || !this.autocompleteResults.hits) return;
    this.showAutocomplete = true;
  };

  private setActiveElement(
    autocompleteEle: HTMLAnchorElement | HTMLButtonElement
  ) {
    this.autocompleteEles.map((ele) =>
      ele.setAttribute('aria-selected', 'false')
    );
    autocompleteEle.focus({ preventScroll: true });
    autocompleteEle.setAttribute('aria-selected', 'true');
  }

  private onAutocompleteInteract = (ev: KeyboardEvent) => {
    // list open, navigate items with arrows
    if (!this.showAutocomplete || !this.autocompleteResults) return;
    let testIndex = this.currAIndex;

    switch (ev.key) {
      case 'Tab':
        setTimeout(() => {
          if (
            !this.el.shadowRoot.activeElement ||
            !this.el.shadowRoot.activeElement.closest('.search-widget')
          ) {
            this.showAutocomplete = false;
            return;
          } else {
            const found = this.autocompleteEles.find((ele, i) => {
              if (ele === this.el.shadowRoot.activeElement) {
                this.currAIndex = i;
                return true;
              }
            });
            if (found) this.setActiveElement(found);
            else this.currAIndex = -1;
          }
        });
        break;
      case 'ArrowDown':
      case 'ArrowUp':
        if (ev.key === 'ArrowDown') testIndex++;
        else if (ev.key === 'ArrowUp') testIndex--;

        const found = this.autocompleteEles[testIndex];
        if (
          found ||
          testIndex < 0 ||
          testIndex > this.autocompleteEles.length - 1
        )
          ev.preventDefault();
        this.searchInput.clientWidth; // force reflow

        if (found) {
          this.setActiveElement(found);
          this.currAIndex = testIndex;
        } else if (testIndex < 0) {
          this.searchInput.setFocus();
          this.currAIndex = -1;
        }
        break;
      case 'Enter':
      case ' ':
        if (this.autocompleteEles[this.currAIndex]) {
          this.autocompleteEles[this.currAIndex].click();
        }
        break;
    }
  };

  // Various.

  /**
   * Assesses what content is present in which slots
   */
  private assessSlottedContent = () => {
    readTask(() => {
      this.hasSiteSlot = !!this.el.querySelectorAll('[slot="site"]').length;
      this.hasLoggedinSlot =
        !!this.el.querySelectorAll('[slot="loggedin"]').length;
      this.hasPromotionSlot =
        !!this.el.querySelectorAll('[slot="promotion"]').length;

      this.aboutSlotLen = this.el.querySelectorAll('[slot="about"]').length;
      this.iconSlotLen = this.el.querySelectorAll('[slot="icon"]').length;
      this.mainSlotLen = this.el.querySelectorAll('[slot="main"]').length;
      this.overflowSlotLen =
        this.el.querySelectorAll('[slot="overflow"]').length;
      this.searchSlotLen = this.el.querySelectorAll('[slot="search"]').length;
    });
  };

  /**
   * Attach the IntersectionObserver - from which all threshold / breakpoint logic hinges
   */
  private attachIO() {
    if (this.io) return;
    const root = this.el.shadowRoot.querySelector('.global-nav .main-bar');
    if (
      Build.isBrowser &&
      typeof window !== 'undefined' &&
      (window as any).IntersectionObserver &&
      !!root
    ) {
      const io = (this.io = new (window as any).IntersectionObserver(
        (data: IntersectionObserverEntry[]) => {
          this.intersectRatio = data.slice(-1)[0].intersectionRatio;
        },
        { root, threshold: 1 }
      ));
      io.observe(root.querySelector('.measure-ele'));
    }
  }

  // Stencil Lifecycle / Rendering

  componentDidLoad() {
    if (Build.isBrowser) {
      this.debounceResize = debounce(this.onWindowResize.bind(this), 300);

      window.addEventListener('resize', this.debounceResize);
      this.currSize = window.innerWidth;
    }

    this.initAlgoliaClient();
    this.addAlgoliaIndeces();
    if (this.remoteDataReady) this.attachIO();
  }

  componentWillLoad() {
    ComponentStore.init(
      this,
      ['searchIndex', 'searchValue'],
      'url-hash',
      'gns'
    );

    this.assessSlottedContent();
  }

  disconnectedCallback() {
    if (this.io) {
      this.io.disconnect();
      this.io = undefined;
    }
    if (this.mo) {
      this.mo.disconnect();
      this.mo = undefined;
    }
    if (Build.isBrowser)
      window.removeEventListener('resize', this.debounceResize);
  }

  connectedCallback() {
    this.handleMyAccountData();
    this.getMyAccData();
  }

  // Render output

  private autocompleteList(): VNode {
    let content;
    if (this.showAutocomplete && this.scrollingUp) {
      let currentScopeHits = 0;
      let scopeChangeSuggestions = 0;

      const scopeSuggestReducer = (
        total: number,
        result: AlgoliaMultiResult
      ) => {
        return result.index !== this.currentIndex.index
          ? total + result.hits.length
          : total;
      };
      const currentSuggestReducer = (
        total: number,
        result: AlgoliaMultiResult
      ) => {
        return result.index === this.currentIndex.index
          ? total + result.hits.length
          : total;
      };

      if (this.scopeSearch) {
        scopeChangeSuggestions = this.scopeSearch.results.reduce(
          scopeSuggestReducer,
          0
        );
        currentScopeHits = this.scopeSearch.results.reduce(
          currentSuggestReducer,
          0
        );
      }

      if (
        (this.autocompleteResults && this.autocompleteResults.hits.length) ||
        (scopeChangeSuggestions && !currentScopeHits)
      ) {
        let scopeSuggestions = 0;

        content = (
          <div>
            {!currentScopeHits && (
              <div class="search-autocomplete-hit search-autocomplete-hit--no-result">
                No results matching '{this.searchValInternal}' were found in{' '}
                <strong>{this.currentIndex.name}</strong>. Try another phrase.
              </div>
            )}
            {this.autocompleteResults && [
              this.autocompleteResults.hits.map((hit, i) => {
                return (
                  <a
                    role="option"
                    ref={(a) => this.autocompleteEles.push(a)}
                    tabindex="-1"
                    id={'autocomplete-hit-' + i}
                    href={hit.url}
                    class="search-autocomplete-hit"
                    innerHTML={this.autocompleteSnippet(hit)}
                  ></a>
                );
              }),
            ]}
            {(!currentScopeHits || this.currentIndex.allGroup) &&
              this.scopeSearch &&
              this.scopeSearch.results && [
                this.scopeSearch.results.map((result, i) => {
                  if (
                    !result.hits.length ||
                    result.index === this.currentIndex.index ||
                    scopeSuggestions > 1
                  )
                    return;
                  scopeSuggestions++;
                  return (
                    <button
                      role="option"
                      ref={(button) => this.autocompleteEles.push(button)}
                      tabindex="-1"
                      id={'autocomplete-scope-' + i}
                      class="search-autocomplete-hit"
                      onClick={() => this.switchIndexSubmit(result.index)}
                      onMouseDown={() => this.switchIndexSubmit(result.index)}
                    >
                      "{this.searchValInternal}"{' '}
                      <div class="search-autocomplete-hit-scope">
                        in {result.indexName}
                      </div>
                    </button>
                  );
                }),
              ]}
            {!!currentScopeHits && (
              <div class="search-autocomplete-foot">
                <button
                  role="option"
                  class="search-autocomplete-submit"
                  ref={(button) => this.autocompleteEles.push(button)}
                  id={'autocomplete-foot'}
                  onClick={this.onSearchSubmit}
                  onMouseDown={this.onSearchSubmit}
                >
                  View all results
                </button>
              </div>
            )}
          </div>
        );
      } else if (!currentScopeHits) {
        content = (
          <div class="search-autocomplete-hit search-autocomplete-hit--no-result">
            No results matching '{this.searchValInternal}' were found. Try
            another phrase.
          </div>
        );
      }
    }

    return (
      <div
        class="search-autocomplete"
        role="listbox"
        id="autocomplete-results"
        aria-expanded={this.showAutocomplete ? 'true' : 'false'}
        hidden={!this.showAutocomplete}
        onKeyDown={this.onAutocompleteInteract}
      >
        {content}
      </div>
    );
  }

  private searchWidget(): VNode[] {
    return [
      <form
        class="search-inputs"
        onSubmit={this.onSearchSubmit}
        id="global-nav-search"
      >
        <nano-select
          class="search-input"
          label="Which area of content would you like to search?"
          hideLabel={true}
          showInlineError={false}
          value={this.searchIndex}
          onNanoChange={this.onSearchIndexChange}
          mask={true}
        >
          {this.internalSearchIndeces.map((index) => {
            return (
              <nano-option
                selected={index.index === this.searchIndex}
                value={index.index}
              >
                {index.name || index.index}
              </nano-option>
            );
          })}
          <nano-icon
            name="light/chevron-down"
            class="select-input__arrow"
            slot="down-arrow"
          />
        </nano-select>
        <nano-input
          class="search-input"
          label="What would you like to search for?"
          aria-label="What would you like to search for"
          hideLabel={true}
          showInlineError={false}
          clear-input
          placeholder="Search Nanopore"
          type="text"
          ref={(input) => (this.searchInput = input)}
          onNanoChange={this.onSearchChange}
          onNanoInput={this.onSearchInput}
          onKeyDown={this.onAutocompleteInteract}
          debounce={500}
          aria-autocomplete="list"
          aria-activedescendant={
            this.autocompleteEles[this.currAIndex]
              ? this.autocompleteEles[this.currAIndex].id
              : false
          }
          aria-controls="autocomplete-results"
          onNanoFocus={this.showAutocompleteResults}
          value={this.searchValue}
          role="textbox"
        >
          <button
            class={{
              'search-icon': true,
              'search-icon--search': true,
              'search-icon--show': !!this.searchValue && !this.searchLoading,
            }}
            slot="end"
            onMouseDown={(e) => {
              this.searchValInternal = this.searchInput.value;
              this.onSearchSubmit(e, true);
            }}
          >
            <nano-icon name="light/search"></nano-icon>
          </button>
          <span
            class={{
              'search-icon': true,
              'search-icon--loader': true,
              'search-icon--show': this.searchLoading,
            }}
            slot="end"
          >
            <nano-icon name="light/spinner-third"></nano-icon>
          </span>
        </nano-input>
        <input type="submit" />
      </form>,
    ];
  }

  render() {
    this.autocompleteEles = [];
    const isRtl = (this.el.ownerDocument as Document).dir === 'rtl';

    const searchWidget = this.searchWidget();
    searchWidget.push(this.autocompleteList());

    return (
      <Host
        class={{
          [this.thresholdClasses().join(' ')]: true,
          ready: this.ready,
          'search-bar-shown': this.searchBarShown && this.scrollingUp,
          'search-auto-complete-shown':
            this.showAutocomplete && this.scrollingUp,
          'modal-open': this.modalIsOpen,
          resizing: this.isResizing,
        }}
        dir={isRtl ? 'rtl' : null}
      >
        <div
          class={{
            'global-nav': true,
            'scrolling-down': !this.scrollingUp,
          }}
        >
          {/* START BURGER / OVERFLOW MENU */}
          <nav
            id="global-nav-menu"
            class={{
              'gn-menu': true,
              open: this.modalOpen,
              'has-promotion': this.hasPromotionSlot,
              'menu-full-screen': this.menuFullScreen,
              'secondary-open': !!this.secondaryMenuOpen,
            }}
            aria-expanded={this.modalOpen ? 'true' : 'false'}
            ref={(div) => (this.menuDiv = div)}
            part="burger"
          >
            <div
              class="gn-menu_wrap"
              ref={(div) => (this.menuWrapDiv = div)}
              tabindex="-1"
              part="burger-menu"
            >
              <div class="gn-menu_actions" part="burger-actions-bar">
                <button
                  class="menu-btn icon-btn icon-btn"
                  onMouseDown={this.onMenuBtnClick}
                  onKeyDown={this.onMenuBtnKeyDown}
                >
                  {this.threshold < this.THRESHOLDLIMIT - 3 && (
                    <nano-icon
                      name="light/times"
                      aria-label="close menu"
                    ></nano-icon>
                  )}
                  {this.threshold >= this.THRESHOLDLIMIT - 3 && (
                    <nano-icon
                      name="light/bars"
                      aria-label="close menu"
                    ></nano-icon>
                  )}
                </button>

                {this.threshold <
                  this.THRESHOLDLIMIT - THRESHOLDBREAKS.icon && [
                  !!this.iconSlotLen && <slot name="icon" />,
                  this.myAccData &&
                    (!this.activeMyAccountSections ||
                      this.activeMyAccountSections.includes('icons')) && [
                      this.msgUrl && (
                        <a href={this.msgUrl} class="icon-btn">
                          <nano-icon name="light/bell"></nano-icon>
                          {this.msgCount > 0 && (
                            <span class="gn-menu_actions-counter">
                              {this.msgCount}
                            </span>
                          )}
                        </a>
                      ),
                      this.cartUrl && (
                        <a href={this.cartUrl} class="icon-btn">
                          <nano-icon name="light/shopping-cart"></nano-icon>
                          {this.cartCount > 0 && (
                            <span class="gn-menu_actions-counter">
                              {this.cartCount}
                            </span>
                          )}
                        </a>
                      ),
                    ],
                ]}

                {this.myAccData && this.myAccData.urls.login && (
                  <a
                    href={this.myAccData.urls.login + this.ssoRedirect}
                    class="login-btn"
                  >
                    Login / Register{' '}
                    <nano-icon name="solid/chevron-right"></nano-icon>
                  </a>
                )}
              </div>

              <div
                class="content-wrap"
                ref={(div) => (this.menuContentDiv = div)}
              >
                <div class="content" part="burger-content">
                  <nav class="nav-links nav-links--main" role="menu">
                    <slot
                      name="overflow"
                      onSlotchange={this.assessSlottedContent}
                    />
                    {this.threshold <
                      this.THRESHOLDLIMIT - THRESHOLDBREAKS.main &&
                      !!this.mainSlotLen && (
                        <slot
                          name="main"
                          onSlotchange={this.assessSlottedContent}
                        />
                      )}
                  </nav>

                  {/* ABOUT AND SITE MENU IN BURGER */}
                  {(((!!this.aboutSlotLen || !!this.aboutNavItms.length) &&
                    this.hasSiteSlot) ||
                    !!this.siteNavItms.length) && (
                    <div>
                      {/* SITES MENU (Always in burger) */}
                      {(this.hasSiteSlot || !!this.siteNavItms.length) && [
                        (this.threshold >= this.THRESHOLDLIMIT - 2 ||
                          this.mainSlotLen < 2) &&
                          this.overflowSlotLen < 2 && (
                            <div class="nav-links-wrap">
                              <h4 class="nav-links_title nav-links_title--sites">
                                Sites
                              </h4>
                              <nav
                                class="nav-links nav-links--sub nav-links--sites"
                                aria-label="Different company site links"
                              >
                                <slot
                                  name="site"
                                  onSlotchange={this.assessSlottedContent}
                                />
                                {this.siteNavItms.map((link) => {
                                  return (
                                    <nano-nav-item class="nano-global-nav nano-global-nav-menu">
                                      <a
                                        href={link.address}
                                        target={link.target}
                                      >
                                        {link.title}
                                      </a>
                                    </nano-nav-item>
                                  );
                                })}
                              </nav>
                            </div>
                          ),
                        ((this.threshold < this.THRESHOLDLIMIT - 2 &&
                          this.mainSlotLen >= 2) ||
                          this.overflowSlotLen >= 2) && (
                          <nav
                            class="nav-links"
                            aria-label="Different company site links"
                          >
                            <nano-nav-item class="nano-global-nav nano-global-nav-menu">
                              Sites
                              <nano-icon
                                slot="icon-end"
                                name="solid/chevron-right"
                              ></nano-icon>
                              <div slot="secondary">
                                <div class="content">
                                  <button
                                    class="back-btn"
                                    onClick={this.subMenuClose}
                                  >
                                    <nano-icon
                                      slot="icon-end"
                                      name="solid/chevron-left"
                                      aria-label="go back"
                                    ></nano-icon>
                                    Sites
                                  </button>
                                  <div class="content--sub">
                                    <slot
                                      name="site"
                                      onSlotchange={this.assessSlottedContent}
                                    />
                                    {this.siteNavItms.map((link) => {
                                      return (
                                        <nano-nav-item class="nano-global-nav nano-global-nav-menu">
                                          <a
                                            href={link.address}
                                            target={link.target}
                                          >
                                            {link.title}
                                          </a>
                                        </nano-nav-item>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </nano-nav-item>
                          </nav>
                        ),
                      ]}
                    </div>
                  )}
                </div>

                {/* USER PROFILE IN BURGER */}
                {this.isLoggedIn &&
                  this.threshold <
                    this.THRESHOLDLIMIT - THRESHOLDBREAKS.login &&
                  (this.loggedInNavItms.length || this.hasLoggedinSlot) && (
                    <nav class="nav-links" aria-label="Your user account links">
                      <nano-nav-item class="nano-global-nav user-nav nano-global-nav-menu">
                        {this.myAccountUser.name}
                        <nano-icon
                          slot="icon-end"
                          name="solid/chevron-right"
                        ></nano-icon>
                        <div slot="secondary">
                          <div class="content">
                            <button
                              class="back-btn"
                              onClick={this.subMenuClose}
                            >
                              <nano-icon
                                slot="icon-end"
                                name="solid/chevron-left"
                                aria-label="go back"
                              ></nano-icon>
                              {this.myAccountUser.name}
                            </button>
                            {!!this.myAccountUser?.small_avatar_url && (
                              <nano-global-nav-user-profile
                                myAccountUser={this.myAccountUser}
                                userProfileUrl={this.userProfileUrl}
                              ></nano-global-nav-user-profile>
                            )}
                            <div class="content--sub">
                              <slot
                                name="loggedin"
                                onSlotchange={this.assessSlottedContent}
                              />
                              {this.loggedInNavItms.map((link) => {
                                return (
                                  <nano-nav-item class="nano-global-nav nano-global-nav-menu">
                                    <a href={link.address} target={link.target}>
                                      {link.title}
                                    </a>
                                  </nano-nav-item>
                                );
                              })}
                              <nano-nav-item class="nano-global-nav nano-global-nav-menu">
                                <a
                                  href={
                                    this.myAccData.urls.forgot_password +
                                    this.ssoRedirect
                                  }
                                >
                                  Change Password
                                </a>
                              </nano-nav-item>
                            </div>
                          </div>
                        </div>
                      </nano-nav-item>
                    </nav>
                  )}

                <slot
                  name="promotion"
                  onSlotchange={this.assessSlottedContent}
                />

                {this.myAccData && this.myAccData.urls.logout && (
                  <div class="login-btn logout-btn">
                    <a href={this.myAccData.urls.logout + this.ssoRedirect}>
                      Logout
                    </a>
                  </div>
                )}
              </div>
            </div>
          </nav>
          {/* END BURGER / OVERFLOW MENU */}

          {/* START MAIN NAV BAR */}
          <div class="sticker-trigger" />
          <nano-sticker
            auto-resize="false"
            break-point-max="800"
            quietMode={{ h: 600, w: 800 }}
            part="sticker"
          >
            <div class="bars" part="bars">
              <nav
                part="main-bar"
                class="main-bar"
                aria-label="Main site navigation"
                tabindex="-1"
                ref={(div) => (this.mainBarDiv = div)}
                role={
                  !!this.mainSlotLen &&
                  this.threshold >= this.THRESHOLDLIMIT - THRESHOLDBREAKS.main
                    ? 'menu'
                    : undefined
                }
              >
                {(this.hasPromotionSlot ||
                  !!this.overflowSlotLen ||
                  ((this.myAccData || !!this.iconSlotLen) &&
                    this.threshold <
                      this.THRESHOLDLIMIT - THRESHOLDBREAKS.icon) ||
                  (this.isLoggedIn &&
                    (!!this.loggedInNavItms.length || this.hasLoggedinSlot) &&
                    this.threshold <
                      this.THRESHOLDLIMIT - THRESHOLDBREAKS.login) ||
                  (!!this.mainSlotLen &&
                    this.threshold <
                      this.THRESHOLDLIMIT - THRESHOLDBREAKS.main) ||
                  ((!!this.aboutSlotLen || !!this.aboutNavItms.length) &&
                    this.threshold <
                      this.THRESHOLDLIMIT - THRESHOLDBREAKS.about) ||
                  this.hasSiteSlot ||
                  !!this.siteNavItms.length) && (
                  <button
                    class="menu-btn icon-btn"
                    onMouseDown={this.menuOpen}
                    onKeyDown={this.onMenuBtnKeyDown}
                    aria-expanded={this.modalOpen ? 'true' : 'false'}
                    aria-controls="global-nav-menu"
                    ref={(btn) => (this.menuBtn = btn)}
                  >
                    <nano-icon
                      name="light/bars"
                      aria-label="open menu"
                    ></nano-icon>
                  </button>
                )}

                {this.showLogo && (
                  <a href={this.logoUrl} class="logo-link" part="logo-link">
                    <img
                      src={getAssetPath(`../nano-assets/ont-logo.svg`)}
                      alt="Oxford Nanopore Technologies Logo"
                      class="logo"
                    />
                  </a>
                )}
                {!this.showLogo && (
                  <div class="logo-link">
                    <slot
                      name="logo"
                      onSlotchange={this.assessSlottedContent}
                    />
                  </div>
                )}

                <div class="nav-links nav-links--main">
                  <slot name="main" onSlotchange={this.assessSlottedContent} />
                </div>

                {/* search widget displayed in the bar (there is enough space to show it) */}
                {this.mainSlotLen === 0 &&
                  this.threshold >=
                    this.THRESHOLDLIMIT - THRESHOLDBREAKS.search && [
                    this.showSearch && !!this.internalSearchIndeces.length && (
                      <div
                        class="search-widget"
                        role="combobox"
                        aria-owns="autocomplete-results"
                        aria-expanded={
                          this.showAutocomplete && this.autocompleteResults
                            ? 'true'
                            : 'false'
                        }
                      >
                        {searchWidget}
                      </div>
                    ),
                    (!this.showSearch || !this.myAccData) &&
                      !!this.searchSlotLen && (
                        <slot
                          name="search"
                          onSlotchange={this.assessSlottedContent}
                        />
                      ),
                  ]}

                {/* Bar Icon buttons */}

                {/* Bar search icon button (when not enough to show search in bar) */}
                {(this.mainSlotLen > 0 ||
                  this.threshold <
                    this.THRESHOLDLIMIT - THRESHOLDBREAKS.search) &&
                  ((this.showSearch && !!this.internalSearchIndeces.length) ||
                    !!this.searchSlotLen) && (
                    <button
                      class={{
                        'icon-btn': true,
                        'search-btn': true,
                        'search-btn--open': this.searchBarShown,
                      }}
                      aria-controls="global-nav-search-bar"
                      aria-expanded={this.searchBarShown ? 'true' : 'false'}
                      onMouseDown={this.onSearchBtnClick}
                      onKeyDown={this.onSearchBtnKeyDown}
                    >
                      <nano-icon name="light/search"></nano-icon>
                    </button>
                  )}

                {/* Bar cart and notifications  */}
                {this.threshold >=
                  this.THRESHOLDLIMIT - THRESHOLDBREAKS.icon && [
                  !!this.iconSlotLen && <slot name="icon" />,
                  this.myAccData &&
                    (!this.activeMyAccountSections ||
                      this.activeMyAccountSections.includes('icons')) && [
                      this.msgUrl && (
                        <a href={this.msgUrl} class="icon-btn">
                          <nano-icon name="light/bell"></nano-icon>
                          {this.msgCount > 0 && (
                            <span class="gn-menu_actions-counter">
                              {this.msgCount}
                            </span>
                          )}
                        </a>
                      ),
                      this.cartUrl && !!this.cartCount && (
                        <a href={this.cartUrl} class="icon-btn">
                          <nano-icon name="light/shopping-cart"></nano-icon>
                          {this.cartCount > 0 && (
                            <span class="gn-menu_actions-counter">
                              {this.cartCount}
                            </span>
                          )}
                        </a>
                      ),
                    ],
                ]}

                {/* Bar about links */}
                {(!!this.aboutSlotLen || !!this.aboutNavItms.length) &&
                  this.threshold >=
                    this.THRESHOLDLIMIT - THRESHOLDBREAKS.about && (
                    <nav
                      class="nav-links nav-links--sub"
                      aria-label="About the company. Links"
                      role="menu"
                    >
                      <slot
                        name="about"
                        onSlotchange={this.assessSlottedContent}
                      />
                      {!!this.aboutNavItms.length && (
                        <nano-nav-item class="nano-global-nav nano-global-nav-bar">
                          <a
                            href={this.aboutNavItms[0].address}
                            target={this.aboutNavItms[0].target}
                          >
                            {this.aboutNavItms[0].title}
                          </a>
                        </nano-nav-item>
                      )}
                    </nav>
                  )}

                {/* Bar login button / User panel */}
                {this.myAccData &&
                  (!this.activeMyAccountSections ||
                    this.activeMyAccountSections.includes('loggedin')) &&
                  this.threshold >=
                    this.THRESHOLDLIMIT - THRESHOLDBREAKS.login && [
                    this.myAccData.urls.login && (
                      <a
                        href={this.myAccData.urls.login + this.ssoRedirect}
                        class="login-btn"
                      >
                        Login / Register
                      </a>
                    ),
                    this.myAccountUser && (
                      <div class="user-links">
                        <button
                          class={{
                            'icon-btn': true,
                            'user-links-btn': true,
                            open: this.userMenuOpen,
                          }}
                          onMouseDown={this.onUserBtnClick}
                          onKeyDown={this.onUserBtnKeyDown}
                        >
                          {' '}
                          {this.myAccountUser.name}
                          <nano-icon name="solid/caret-down"></nano-icon>
                        </button>
                        <div
                          class="user-links-panel"
                          tabindex="-1"
                          ref={(div) => (this.userLinkPanel = div)}
                        >
                          {!!this.myAccountUser?.small_avatar_url && (
                            <nano-global-nav-user-profile
                              myAccountUser={this.myAccountUser}
                              userProfileUrl={this.userProfileUrl}
                            ></nano-global-nav-user-profile>
                          )}
                          <div class="user-links-panel-content">
                            {this.loggedInNavItms.map((link) => {
                              return (
                                <a href={link.address} target={link.target}>
                                  {link.title}
                                </a>
                              );
                            })}
                          </div>
                          <div class="user-links-panel-foot">
                            <a
                              href={
                                this.myAccData.urls.forgot_password +
                                this.ssoRedirect
                              }
                            >
                              Change Password
                            </a>
                            <a
                              href={
                                this.myAccData.urls.logout + this.ssoRedirect
                              }
                            >
                              Logout
                            </a>
                          </div>
                        </div>
                      </div>
                    ),
                  ]}
                <span class="measure-ele"></span>
              </nav>

              {/* Search widget shown in bar underneath main bar when there is not enough space  */}
              {(this.mainSlotLen > 0 ||
                this.threshold <
                  this.THRESHOLDLIMIT - THRESHOLDBREAKS.search) && (
                <div
                  id="global-nav-search-bar"
                  class={{
                    'search-bar': true,
                    show: this.searchBarShown,
                  }}
                  aria-expanded={this.searchBarShown ? 'true' : 'false'}
                  role="region"
                  tabindex="-1"
                  ref={(div) => (this.searchBarEl = div)}
                  part="search-bar"
                >
                  <div class="search-widget">
                    {this.showSearch && !!this.internalSearchIndeces.length && (
                      <div>{searchWidget}</div>
                    )}
                    {(!this.showSearch || !this.myAccData) &&
                      !!this.searchSlotLen && (
                        <slot
                          name="search"
                          onSlotchange={this.assessSlottedContent}
                        />
                      )}
                  </div>
                </div>
              )}
            </div>
          </nano-sticker>
          {/* END MAIN NAV BAR */}

          {(this.threshold < this.THRESHOLDLIMIT - 3 ||
            this.menuFullScreen) && (
            <div
              class={{
                mask: true,
                open: this.modalOpen,
              }}
              onClick={this.menuClose}
              onTouchEnd={this.menuClose}
            ></div>
          )}
        </div>
        <div class="site-content">
          <slot />
        </div>
      </Host>
    );
  }

  // Data processing

  private domainFor(origin: string): string {
    if (!this.myAccData) return '';
    const domain = this.myAccData.domains.find((dm) => dm.origin === origin);
    return domain ? domain.domain : '';
  }

  private processSearchResults(results: AlgoliaMultiResults | AlgoliaResults) {
    const multiResults = results as AlgoliaMultiResults;
    const singleResult = results as AlgoliaResults;

    if (multiResults.results) {
      multiResults.results.forEach((result, i) => {
        multiResults.results[i] = this.processSearchResults(
          result
        ) as AlgoliaResults;
      });
      return multiResults;
    } else {
      singleResult.hits.map((hit) => {
        if (hit.url && !hit.url.match(/^http/))
          hit.url =
            location.protocol + '//' + this.domainFor(hit.origin) + hit.url;
      });
      return singleResult;
    }
  }

  private processMyAccData() {
    if (!this.myAccData || !this.myAccData.search.indeces.length) return;

    if (this.myAccData.search.api_key)
      this.searchApiKey = this.myAccData.search.api_key;
    if (this.myAccData.search.app_id)
      this.searchAppId = this.myAccData.search.app_id;

    if (this.myAccData.cart.count) this.cartCount = this.myAccData.cart.count;
    if (this.myAccData.notifications.count)
      this.msgCount = this.myAccData.notifications.count;

    if (!this.searchAppId || !this.searchApiKey) return;
    this.internalSearchIndeces = [
      ...this.myAccData.search.indeces,
      ...this.searchIndeces,
    ];

    if (this.myAccData.urls.cart && !this.cartUrl)
      this.cartUrl = this.myAccData.urls.cart;
    if (this.myAccData.urls.messages && !this.msgUrl)
      this.msgUrl = this.myAccData.urls.messages;
  }

  private processMyAccLinks() {
    if (!this.myAccData.links || !this.myAccData.links.length) return;

    this.myAccData.links.forEach((link) => {
      switch (link.area) {
        case 'left-side-logged-in':
        case 'left-side-logged-out':
          if (
            this.activeMyAccountSections &&
            !this.activeMyAccountSections.includes('sites')
          )
            break;
          if (this.isLoggedIn && link.area === 'left-side-logged-in')
            this.siteNavItms.push(link);
          else if (!this.isLoggedIn && link.area === 'left-side-logged-out')
            this.siteNavItms.push(link);
          break;
        case 'right-side-logged-in':
        case 'right-side-logged-out':
          if (
            this.activeMyAccountSections &&
            !this.activeMyAccountSections.includes('about')
          )
            break;
          if (this.isLoggedIn && link.area === 'right-side-logged-in')
            this.aboutNavItms.push(link);
          else if (!this.isLoggedIn && link.area === 'right-side-logged-out')
            this.aboutNavItms.push(link);
          break;
        case 'profile-panel-list':
          if (
            this.activeMyAccountSections &&
            !this.activeMyAccountSections.includes('loggedin')
          )
            break;
          this.loggedInNavItms.push(link);
          break;
      }
    });
  }
}
